import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useColors } from '@mastimanch/ui-components';
import { useAuthContext } from '../auth-config';
import toast from 'react-hot-toast';
import { FiMail, FiLock, FiArrowRight, FiCheck, FiEye, FiEyeOff } from 'react-icons/fi';

const ForgotPasswordPage: React.FC = () => {
  const colors = useColors();
  const navigate = useNavigate();
  const { forgotPassword, confirmForgotPassword } = useAuthContext();

  const [step, setStep] = useState(1); // 1: Enter email, 2: Enter code & new password
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validatePassword = (pass: string) => {
    if (pass.length < 8) return false;
    const hasUpper = /[A-Z]/.test(pass);
    const hasLower = /[a-z]/.test(pass);
    const hasDigit = /[0-9]/.test(pass);
    const hasSpecial = /[!@#$%^&*(),.?"{}|<>_+=\-[\]\\\/]/.test(pass);
    return hasUpper && hasLower && hasDigit && hasSpecial;
  };

  // Password requirements checks
  const meetsMinLength = newPassword.length >= 8;
  const hasUppercase = /[A-Z]/.test(newPassword);
  const hasLowercase = /[a-z]/.test(newPassword);
  const hasDigit = /[0-9]/.test(newPassword);
  const hasSpecial = /[!@#$%^&*(),.?"{}|<>_+=\-[\]\\\/]/.test(newPassword);

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setErrors({ email: 'Email address is required.' });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrors({ email: 'Please enter a valid email address.' });
      return;
    }

    setErrors({});

    try {
      setIsLoading(true);
      await forgotPassword(email);
      toast.success('Reset code sent successfully to your email!');
      setStep(2);
    } catch (err: any) {
      console.error('Forgot password request error:', err);
      toast.error(err.message || 'Failed to send reset code. Please check your email.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};

    if (!code || code.length !== 6) {
      newErrors.code = 'Please enter the 6-digit confirmation code.';
    }

    if (!newPassword) {
      newErrors.newPassword = 'New password is required.';
    } else if (!validatePassword(newPassword)) {
      newErrors.newPassword = 'Password must meet all complexity requirements.';
    }

    if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match.';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    try {
      setIsLoading(true);
      await confirmForgotPassword({
        email,
        code,
        newPassword,
      });
      toast.success('Password reset successful! You can now log in.');
      navigate('/login');
    } catch (err: any) {
      console.error('Password confirm reset error:', err);
      toast.error(err.message || 'Failed to reset password. Please check the code.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-6 bg-gradient-to-br from-[#fffbeb] via-[#ffe4e6] to-[#e0f2fe] overflow-hidden select-none font-sans text-slate-800">
      
      {/* Background SVG illustrations representing learning/fun */}
      <div className="absolute top-10 left-10 w-28 h-28 opacity-10 pointer-events-none text-[#ff6b35]">
        <svg fill="currentColor" viewBox="0 0 24 24"><path d="M12 3L1 9l11 6 9-4.91V17h2V9L12 3z" /></svg>
      </div>
      <div className="absolute bottom-10 right-10 w-36 h-36 opacity-10 pointer-events-none text-[#1b4ddb]">
        <svg fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H7c0-2.76 2.24-5 5-5s5 2.24 5 5c0 1.04-.42 1.99-1.07 2.75z" /></svg>
      </div>

      {/* Main Card */}
      <div className="relative w-full max-w-md bg-white/80 backdrop-blur-md rounded-3xl shadow-2xl border border-white/50 p-8 space-y-6 transition-all duration-300 z-10">
        
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center mb-1">
            <img 
              alt="Masti Manch Logo" 
              className="h-14 object-contain" 
              src="/images/mmlogo.png"
            />
          </div>
          <span className="text-3xl block">🔑</span>
          <h2 className="text-2xl font-extrabold text-[#1a2d54]">
            {step === 1 ? 'Reset Password' : 'New Password'}
          </h2>
          <p className="text-xs text-gray-500 font-semibold max-w-xs mx-auto">
            {step === 1 
              ? 'Enter your student email and we will send you a verification code.' 
              : `We have sent a verification code to ${email}`}
          </p>
        </div>

        {/* STEP 1: Enter email */}
        {step === 1 && (
          <form onSubmit={handleRequestCode} className="space-y-5">
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-wide text-gray-400">
                Email Address
              </label>
              <div className="relative">
                <FiMail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input 
                  type="email" 
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email) setErrors({});
                  }}
                  placeholder="e.g. student@school.com"
                  disabled={isLoading}
                  className="w-full pl-12 pr-4 py-3.5 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium text-sm text-[#1b2559] bg-white/70 transition-all"
                  style={{
                    borderColor: errors.email ? '#ef4444' : undefined,
                  }}
                />
              </div>
              {errors.email && (
                <p className="text-red-500 text-xs font-bold pl-1">{errors.email}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-4 rounded-2xl text-white font-bold bg-[#1b4ddb] hover:bg-blue-700 transition-all flex items-center justify-center space-x-2 text-sm shadow-md active:scale-95 transform"
            >
              {isLoading ? (
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              ) : (
                <>
                  <span>Send Reset Code</span>
                  <FiArrowRight size={16} />
                </>
              )}
            </button>
          </form>
        )}

        {/* STEP 2: Code and new password */}
        {step === 2 && (
          <form onSubmit={handleResetPassword} className="space-y-5">
            {/* Code */}
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-wide text-gray-400">
                Verification Code
              </label>
              <input 
                type="text" 
                maxLength={6}
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.replace(/\D/g, ''));
                  if (errors.code) setErrors(prev => { const n = { ...prev }; delete n.code; return n; });
                }}
                placeholder="6-digit code"
                disabled={isLoading}
                className="w-full px-4 py-3.5 rounded-2xl border border-gray-200 text-center tracking-widest font-black text-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all text-[#1b2559] bg-white/70"
                style={{
                  borderColor: errors.code ? '#ef4444' : undefined,
                }}
              />
              {errors.code && (
                <p className="text-red-500 text-xs font-bold pl-1">{errors.code}</p>
              )}
            </div>

            {/* Password */}
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-wide text-gray-400">
                New Password
              </label>
              <div className="relative">
                <FiLock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input 
                  type={showPassword ? 'text' : 'password'} 
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    if (errors.newPassword) setErrors(prev => { const n = { ...prev }; delete n.newPassword; return n; });
                  }}
                  placeholder="••••••••"
                  disabled={isLoading}
                  className="w-full pl-12 pr-12 py-3.5 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium text-sm text-[#1b2559] bg-white/70 transition-all"
                  style={{
                    borderColor: errors.newPassword ? '#ef4444' : undefined,
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  disabled={isLoading}
                >
                  {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                </button>
              </div>

              {/* Password complexity helper */}
              <div className="p-3 bg-gray-50/80 rounded-2xl border border-gray-100 text-[10px] space-y-1.5 font-bold">
                <span className="text-gray-400 block uppercase tracking-wider">Password Requirements:</span>
                <div className="grid grid-cols-2 gap-2 text-gray-500">
                  <div className={`flex items-center space-x-1.5 transition-colors ${meetsMinLength ? 'text-emerald-600' : 'text-gray-400'}`}>
                    <FiCheck size={12} className={meetsMinLength ? 'text-emerald-500' : 'text-gray-300'} />
                    <span>Min 8 characters</span>
                  </div>
                  <div className={`flex items-center space-x-1.5 transition-colors ${hasUppercase ? 'text-emerald-600' : 'text-gray-400'}`}>
                    <FiCheck size={12} className={hasUppercase ? 'text-emerald-500' : 'text-gray-300'} />
                    <span>1 uppercase letter</span>
                  </div>
                  <div className={`flex items-center space-x-1.5 transition-colors ${hasLowercase ? 'text-emerald-600' : 'text-gray-400'}`}>
                    <FiCheck size={12} className={hasLowercase ? 'text-emerald-500' : 'text-gray-300'} />
                    <span>1 lowercase letter</span>
                  </div>
                  <div className={`flex items-center space-x-1.5 transition-colors ${hasDigit ? 'text-emerald-600' : 'text-gray-400'}`}>
                    <FiCheck size={12} className={hasDigit ? 'text-emerald-500' : 'text-gray-300'} />
                    <span>1 numeric digit</span>
                  </div>
                  <div className={`flex items-center space-x-1.5 transition-colors ${hasSpecial ? 'text-emerald-600' : 'text-gray-400'}`}>
                    <FiCheck size={12} className={hasSpecial ? 'text-emerald-500' : 'text-gray-300'} />
                    <span>1 special char</span>
                  </div>
                </div>
              </div>
              {errors.newPassword && (
                <p className="text-red-500 text-xs font-bold pl-1">{errors.newPassword}</p>
              )}
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-wide text-gray-400">
                Confirm Password
              </label>
              <div className="relative">
                <FiLock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input 
                  type="password" 
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (errors.confirmPassword) setErrors(prev => { const n = { ...prev }; delete n.confirmPassword; return n; });
                  }}
                  placeholder="••••••••"
                  disabled={isLoading}
                  className="w-full pl-12 pr-4 py-3.5 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium text-sm text-[#1b2559] bg-white/70 transition-all"
                  style={{
                    borderColor: errors.confirmPassword ? '#ef4444' : undefined,
                  }}
                />
              </div>
              {errors.confirmPassword && (
                <p className="text-red-500 text-xs font-bold pl-1">{errors.confirmPassword}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-4 mt-2 rounded-2xl text-white font-bold bg-[#1b4ddb] hover:bg-blue-700 transition-all flex items-center justify-center space-x-2 text-sm shadow-md active:scale-95 transform"
            >
              {isLoading ? (
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              ) : (
                <>
                  <span>Reset Password</span>
                  <FiArrowRight size={16} />
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => {
                setStep(1);
                setCode('');
                setNewPassword('');
                setConfirmPassword('');
                setErrors({});
              }}
              className="w-full py-2.5 rounded-2xl font-semibold text-gray-500 hover:bg-gray-150/40 transition text-xs"
            >
              Back to Email Input
            </button>
          </form>
        )}

        {/* Footer */}
        <div className="pt-4 border-t border-gray-100 text-center text-xs font-semibold text-gray-500">
          <span>Remember your password? </span>
          <Link to="/login" className="text-[#1b4ddb] hover:underline font-bold ml-1">
            Back to Log In
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
